<header class="w-full fixed top-0 z-[1033]" [class.header-navbar]="showNavbarBg()">
    <!-- Base layer: solid background (always present on home page) -->
    @if (isHomePage()) {
        <div class="header-home-scrolled absolute inset-0 -z-10"></div>
    }

    <!-- Top layer: transparent hero background (fades in/out) -->
    <div
        class="bg-deep-blue-900 absolute inset-0 -z-10 transition-opacity duration-300 ease-in-out"
        [class.opacity-100]="isTransparent()"
        [class.opacity-0]="!isTransparent()"
        [class.pointer-events-none]="!isTransparent()"
    ></div>

    <tran-skip-link targetId="main-content" labelKey="header.skipToContent" />
    <tran-skip-link targetId="site-footer" labelKey="header.skipToFooter" />

    <div class="mx-5 sm:!mx-20 xl:!mx-auto max-w-7xl flex flex-col !py-[25px] gap-8">
        <!-- First Row: Logo | Skip Link (center) | Language + User Avatar -->
        <div class="flex items-center justify-between">
            <!-- Left: Siemens Logo -->
            <a
                [routerLink]="'/'"
                [attr.aria-label]="'Siemens'"
                class="flex self-stretch items-center box-content w-[var(--element-brand-logo-width)] min-h-[var(--element-brand-logo-height)] [content:var(--element-brand-logo)_/_var(--element-brand-logo-text)] flex-shrink-0"
            ></a>

            <!-- Right: Language Selector + User Avatar -->
            <div class="flex items-center !gap-4">
                <!-- Language Selector -->
                <div id="language-selection-container" class="hidden md:block">
                    <button
                        type="button"
                        class="flex items-center gap-1 font-medium uppercase px-2 py-1"
                        (openChange)="onLanguageDropdownToggle($event)"
                        [siHeaderDropdownTriggerFor]="languageDropdown"
                        [attr.aria-label]="'header.language-selector' | translate"
                        [attr.aria-expanded]="languageDropdownOpen()"
                        aria-haspopup="listbox"
                    >
                        <i class="icon element-global text-lg"></i>
                        <span>{{ currentLanguage() }}</span>
                    </button>
                </div>

                <!-- User Avatar -->
                @if (userDetails) {
                    <tran-user-avatar class="hidden md:block" [userDetails]="userDetails" />
                }

                <!-- Mobile Menu Toggle -->
                <button
                    type="button"
                    class="flex md:hidden items-center justify-center w-10 h-10 cursor-pointer"
                    (click)="toggleMobileMenu()"
                    [attr.aria-expanded]="mobileMenuOpen()"
                    aria-controls="mobile-menu"
                    [attr.aria-label]="'header.toggleMenu' | translate"
                >
                    <i class="icon text-2xl" [class]="mobileMenuOpen() ? 'element-cancel' : 'element-menu'"></i>
                </button>
            </div>
        </div>

        <!-- Second Row: Primary Nav (left) | Secondary Nav (right) -->
        <nav class="hidden md:flex items-center justify-between" aria-label="Main navigation">
            <!-- Primary Navigation (Left) -->
            <ul class="flex items-center gap-8 lg:gap-10">
                @for (item of primaryNavItems(); track item.route) {
                    <li>
                        <a
                            [routerLink]="item.route"
                            routerLinkActive="border-b-[3px] border-[var(--element-ui-0-hover)]"
                            [routerLinkActiveOptions]="{ exact: true }"
                            class="py-2 text-md lg:text-lg !no-underline hover:!no-underline hover:text-[var(--element-ui-0-hover)]"
                        >
                            {{ item.label | translate }}
                        </a>
                    </li>
                }
            </ul>

            <!-- Secondary Navigation (Right) -->
            <ul class="flex items-center gap-6 lg:gap-8">
                @for (item of secondaryNavItems(); track item.route) {
                    <li>
                        @if (item.external) {
                            <a [href]="item.route" target="_blank" class="hover:text-[var(--element-ui-0-hover)]">
                                {{ item.label | translate }}
                            </a>
                        } @else {
                            <a
                                [routerLink]="item.route"
                                routerLinkActive="border-b-[3px] border-[var(--element-ui-0-hover)]"
                                [routerLinkActiveOptions]="{ exact: true }"
                                class="!no-underline hover:!no-underline hover:text-[var(--element-ui-0-hover)]"
                            >
                                {{ item.label | translate }}
                            </a>
                        }
                    </li>
                }
            </ul>
        </nav>
    </div>

    <!-- Mobile Menu Overlay -->
    @if (mobileMenuOpen()) {
        <div
            id="mobile-menu"
            class="fixed inset-0 top-[90px] bg-[var(--element-base-1)] z-50 md:hidden animate-fadeIn"
            role="dialog"
            aria-modal="true"
            [attr.aria-label]="'header.mobileNavMenu' | translate"
        >
            <nav class="h-full overflow-y-auto" [attr.aria-label]="'header.mobileNavMenu' | translate">
                <div class="mx-5 sm:mx-20 py-6">
                    <!-- User Info in Mobile Menu -->
                    @if (userDetails) {
                        <div class="flex items-center gap-3 pb-6 mb-6 border-b border-white/20">
                            <tran-user-avatar [userDetails]="userDetails" />
                        </div>
                    }

                    <!-- Primary Navigation Items -->
                    <ul class="flex flex-col !gap-4 mb-6">
                        @for (item of primaryNavItems(); track item.route) {
                            <li>
                                <a
                                    [routerLink]="item.route"
                                    routerLinkActive="border-l-[3px] border-[var(--element-ui-0-hover)] pl-2"
                                    [routerLinkActiveOptions]="{ exact: true }"
                                    class="block text-lg py-2 !no-underline hover:!no-underline hover:text-[var(--element-ui-0-hover)]"
                                    (click)="closeMobileMenu()"
                                >
                                    {{ item.label | translate }}
                                </a>
                            </li>
                        }
                    </ul>

                    <!-- Secondary Navigation Items -->
                    @if (secondaryNavItems().length > 0) {
                        <div class="pt-6 border-t border-white/20">
                            <ul class="flex flex-col !gap-4">
                                @for (item of secondaryNavItems(); track item.route) {
                                    <li>
                                        @if (item.external) {
                                            <a
                                                [href]="item.route"
                                                target="_blank"
                                                class="block text-base py-2 hover:text-[var(--element-ui-0-hover)]"
                                                (click)="closeMobileMenu()"
                                            >
                                                {{ item.label | translate }}
                                            </a>
                                        } @else {
                                            <a
                                                [routerLink]="item.route"
                                                routerLinkActive="border-l-[3px] border-[var(--element-ui-0-hover)] pl-4"
                                                [routerLinkActiveOptions]="{ exact: true }"
                                                class="block text-base py-2 !no-underline hover:!no-underline hover:text-[var(--element-ui-0-hover)]"
                                                (click)="closeMobileMenu()"
                                            >
                                                {{ item.label | translate }}
                                            </a>
                                        }
                                    </li>
                                }
                            </ul>
                        </div>
                    }

                    <!-- Mobile Language Selector -->
                    <div class="mt-6 pt-6 border-t border-white/20">
                        <div class="flex gap-3">
                            @for (lang of languages; track lang.code) {
                                <button type="button" class="btn btn-primary" (click)="onLanguageSelect(lang.code)">
                                    {{ lang.label }}
                                </button>
                            }
                        </div>
                    </div>
                </div>
            </nav>
        </div>
    }
</header>

<!-- Language Dropdown Template (Desktop) -->
<ng-template #languageDropdown>
    <si-header-dropdown>
        @for (lang of languages; track lang.code) {
            <button si-header-dropdown-item (click)="onLanguageSelect(lang.code)">
                {{ lang.label }}
            </button>
        }
    </si-header-dropdown>
</ng-template>
